'use client';

import { Box, Stack } from '@mui/material';
import { EonAccountStatus } from '@repo/api-gw-sdk';
import classNames from 'classnames';
import { redirect, usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import { Link } from '@/components/shared/link';
import { UserCircle } from '@/components/users/userBadge';
import { useDAL } from '@/contexts/useDAL';
import { useEnvironment } from '@/contexts/useEnvironment';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { useRoles } from '@/contexts/useRoles';
import { useUser } from '@/contexts/useUser';

import { AssistantMenuItem, MenuItem, NavigationActions } from './components';
import { UserBox } from './userBox';

import { EONLogo } from '../shared/eonLogo';

export const Navigation = () => {
  const dal = useDAL();
  const { data: projects } = dal.projects.list();
  const { dlsgFrontend } = useFeatureFlags();
  const { demoSettings } = useEnvironment();
  const pathname = usePathname();
  const { user } = useUser();
  const [forceOpen, setForceOpen] = useState(false);
  const onboardingMode =
    user?.eonAccount.status === EonAccountStatus.EON_ACCOUNT_STATUS_ONBOARDING;
  const registrationMode =
    user?.eonAccount.status ===
    EonAccountStatus.EON_ACCOUNT_STATUS_REGISTRATION;
  const { isAuthorized, rolesMap } = useRoles();

  const hasAccountRoles = Object.keys(user?.roleToProjectsMappings || {}).some(
    (roleId) => {
      const role = rolesMap[roleId];
      return role && !role.isProjectRole;
    }
  );

  useEffect(() => {
    if (!user || user.givenName?.toLowerCase().includes('eon support')) return;

    if (onboardingMode && !pathname.includes('/global-settings')) {
      redirect('/global-settings');
    } else if (registrationMode) {
      redirect('/onboarding/in-progress');
    }
  }, [onboardingMode, pathname, registrationMode, user]);

  return (
    <Box
      data-testid='navigation'
      className={classNames({
        open: forceOpen,
      })}
      sx={{
        minWidth: '60px',
        maxWidth: '60px',
        position: 'sticky',
        height: '100%',
        zIndex: 1000,

        '& .slide-left, .logo-link *, .user-badge, .docs-link': {
          transition: `opacity 500ms ease-in-out, padding-left 300ms ease-in-out, margin-left 300ms ease-in-out, visibility 500ms ease-in-out`,
        },

        '& a': {
          display: 'flex',
          alignItems: 'center',
          color: 'white',
          height: '60px',
          position: 'relative',
          width: '100%',
        },

        '& .user-box, .navigation-actions, a': {
          pointerEvents: 'none',
        },

        '&:hover, &.open': {
          '& .user-badge': {
            opacity: 0,
          },

          '& .docs-link': {
            opacity: 0,
          },

          '& > div': {
            minWidth: '308px',
          },

          '& .slide-left': {
            opacity: '1 !important',
            visibility: 'visible !important',
            paddingLeft: '3px',
            pointerEvents: 'all',

            a: {
              pointerEvents: 'all',
            },
          },

          '& .logo-link img': {
            marginLeft: '19px !important',
          },
        },
      }}
    >
      <Stack
        justifyContent='space-between'
        sx={{
          backgroundColor: 'var(--mui-palette-background-black1)',
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          height: '100%',
          minWidth: '60px',
          transition: 'min-width 300ms ease-in-out',
        }}
      >
        <Stack>
          <Stack
            className={classNames({
              'min-h-[200px]': !hasAccountRoles,
              'min-h-[280px]': hasAccountRoles,
              'min-h-[300px]': (projects?.projects || []).length > 1,
            })}
          >
            <Link href='/' className='slide-left logo-link'>
              <>
                <EONLogo className='ml-[-16px]' />
                <Box
                  className='w-[24px] h-[24px] absolute left-[-4px]'
                  sx={{
                    backgroundColor: 'var(--mui-palette-background-black1)',
                  }}
                />
              </>
            </Link>
            <Box className='relative'>
              <Stack
                className='w-[60px] h-[60px] user-badge opacity-1'
                alignItems='center'
                justifyContent='center'
              >
                <UserCircle user={user} />
              </Stack>
              <UserBox
                hasAccountRoles={hasAccountRoles}
                projects={projects?.projects}
                setForceOpen={setForceOpen}
              />
            </Box>
          </Stack>
          <Stack>
            <MenuItem
              key='dashboard'
              href='/dashboard'
              iconClass='material-symbols-team-dashboard-outline'
              title='Dashboard'
              disabled={!isAuthorized('read:dashboard')}
            />
            <MenuItem
              key='inventory'
              data-testid='/inventory'
              href='/inventory'
              iconClass='material-symbols-inventory-2-outline'
              title='Inventory'
              disabled={!isAuthorized('read:inventory')}
            />
            {!demoSettings.ai && (
              <MenuItem
                key='search'
                href='/search'
                iconClass='material-symbols-search'
                title='Search'
                disabled={
                  !isAuthorized('read:db') && !isAuthorized('read:files')
                }
              />
            )}
            <MenuItem
              key='jobs'
              href='/jobs'
              iconClass='material-symbols-checklist-rounded'
              title='Jobs'
              disabled={!isAuthorized('read:jobs')}
            />
            <MenuItem
              key='audit-logs'
              href='/audit-logs'
              iconClass='material-symbols-content-paste-rounded'
              title='Audit logs'
              disabled={!isAuthorized('read:audit_logs')}
            />
            <MenuItem
              key='settings'
              href='/settings'
              iconClass='material-symbols-settings-outline-rounded'
              title='Backup settings'
              disabled={!isAuthorized('read:backup_settings')}
            />
            {dlsgFrontend && (
              <MenuItem
                key='storage-gateway'
                href='/storage-gateway'
                iconClass='material-symbols-door-open-outline-rounded'
                title='Storage gateway'
                disabled={!isAuthorized('read:policies')}
              />
            )}
            {demoSettings.ai && (
              <AssistantMenuItem
                key='assistant'
                href='/assistant'
                title='Search & explore your data'
              />
            )}
          </Stack>
        </Stack>
        <Stack className='relative justify-end pb-[12px]'>
          <Box className='docs-link opacity-1'>
            <MenuItem
              key='docs'
              href='/docs'
              iconClass='material-symbols-help-outline-rounded'
              title=''
            />
          </Box>
          <NavigationActions setForceOpen={setForceOpen} />
        </Stack>
      </Stack>
    </Box>
  );
};
